<!-- GastoCard.vue -->
<template>
  <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
    <header class="mb-8 text-center sm:text-left">
      <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
        Gastos de
        <span class="bg-clip-text text-transparent bg-gradient-to-r from-[#7B2CBF] to-[#A367E1]">Cartões de
          Crédito</span>
      </h1>
      <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">Acompanhe, gerencie e analise suas compras parceladas.
      </p>
    </header>

    <div class="flex justify-center mb-8">
      <nav class="segmented-control" role="tablist">
        <button @click="activeTab = 'list'" :class="{ 'active': activeTab === 'list' }" role="tab">
          <span class="material-symbols-outlined">list_alt</span>
          Gastos
        </button>
        <button @click="activeTab = 'analytics'" :class="{ 'active': activeTab === 'analytics' }" role="tab">
          <span class="material-symbols-outlined">monitoring</span>
          Análise de Gastos
        </button>
      </nav>
    </div>

    <transition name="fade" mode="out-in">
      <div v-if="activeTab === 'list'" key="list">
        <div class="panel mb-6">
          <div class="flex flex-wrap items-center gap-4">
            <button @click="handleNovaCompra" class="btn btn-primary">
              <span class="material-symbols-outlined mr-2">add_shopping_cart</span>
              Adicionar Compra
            </button>
            <button @click="exportToCSV" class="btn btn-secondary">
              <span class="material-symbols-outlined mr-2">download</span>
              Exportar Tabela
            </button>
          </div>
          <hr class="my-4 border-slate-200/80 dark:border-slate-700/60" />
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select v-model="statusFiltro" class="form-select">
              <option value="">Status</option>
              <option value="PENDENTE">Pendente</option>
              <option value="PAGO">Pago</option>
              <option value="CONGELADO">Congelado</option>
            </select>
            <select v-model="bancoFiltro" class="form-select">
              <option value="">Banco</option>
              <option v-for="banco in bancosDisponiveis" :key="banco" :value="banco">{{ banco }}</option>
            </select>
            <select v-model="categoriaFiltro" class="form-select">
              <option value="">Categoria</option>
              <option v-for="cat in categoriasDisponiveis" :key="cat" :value="cat">{{ cat }}</option>
            </select>
            <div class="flex gap-3 col-span-2 md:col-span-1">
              <select v-model="mesSelecionado" class="form-select w-full">
                <option value="">Mês</option>
                <option v-for="mes in meses" :key="mes.valor" :value="mes.valor">{{ mes.nome }}</option>
              </select>
              <select v-model="anoSelecionado" class="form-select w-full">
                <option value="">Ano</option>
                <option v-for="ano in anosDisponiveis" :key="ano" :value="ano">{{ ano }}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="panel hidden sm:block">
          <div class="overflow-x-auto">
            <table class="w-full min-w-[900px] text-sm text-left">
              <thead class="text-xs text-slate-500 dark:text-slate-400 uppercase">
                <tr>
                  <th class="px-6 py-3 font-semibold">Descrição</th>
                  <th class="px-6 py-3 font-semibold text-center">Categoria</th>
                  <th class="px-6 py-3 font-semibold text-center">Parcela</th>
                  <th class="px-6 py-3 font-semibold">Valor</th>
                  <th class="px-6 py-3 font-semibold">Vencimento</th>
                  <th class="px-6 py-3 font-semibold">Quitação</th>
                  <th class="px-6 py-3 font-semibold">Status</th>
                  <th class="px-6 py-3 font-semibold text-center">Ações</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-200/80 dark:divide-slate-700/60">
                <tr v-if="sortedGastos.length === 0">
                  <td colspan="8" class="empty-state h-48">Nenhuma compra encontrada para os filtros selecionados.</td>
                </tr>
                <tr v-for="gastoItem in sortedGastos" :key="gastoItem.id"
                  class="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors duration-150 ease-in-out">
                  <td class="px-6 py-4 font-medium text-slate-900 dark:text-white whitespace-nowrap truncate max-w-xs">
                    <div class="flex items-center gap-3">
                      <div class="w-2 h-8 rounded-full" :style="{ backgroundColor: getBankColor(gastoItem.banco) }">
                      </div>
                      <img :src="getBankLogo(gastoItem.banco)" :alt="gastoItem.banco"
                        class="w-8 h-8 rounded-full shadow-sm object-contain p-1 bg-white">
                      <span class="truncate">{{ gastoItem.descricao }}</span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-center text-slate-500 dark:text-slate-400">
                    <span :title="gastoItem.categoria" class="material-symbols-outlined text-lg">{{
                      getCategoryIcon(gastoItem.categoria) }}</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-center text-slate-500 dark:text-slate-400">{{
                    gastoItem.numeroParcela }}/{{ gastoItem.totalParcelas }}</td>
                  <td class="px-6 py-4 whitespace-nowrap font-semibold text-slate-700 dark:text-slate-200">{{
                    formatCurrency(gastoItem.valorParcela, false) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-slate-500 dark:text-slate-400">{{
                    formatDate(gastoItem.dataPagamento) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-slate-500 dark:text-slate-400">{{
                    formatDate(gastoItem.dataQuitacaoReal) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <select v-model="gastoItem.status" @change="updateStatus(gastoItem)" class="form-select-sm"
                      :class="getStatusClass(gastoItem).text">
                      <option value="PENDENTE">Pendente</option>
                      <option value="PAGO">Pago</option>
                      <option value="CONGELADO">Congelado</option>
                    </select>
                  </td>
                  <td class="px-6 py-4 text-center whitespace-nowrap">
                    <div class="flex items-center justify-center gap-1">
                      <button @click="handleEditParcela(gastoItem)" title="Editar Parcela"
                        class="btn-action-icon text-sky-600 dark:text-sky-400">
                        <span class="material-symbols-outlined text-lg">edit</span>
                      </button>
                      <button @click="confirmDelete(gastoItem)" title="Excluir Parcela"
                        class="btn-action-icon text-rose-500 dark:text-rose-400">
                        <span class="material-symbols-outlined text-lg">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot class="bg-slate-50 dark:bg-slate-900/50 font-semibold">
                <tr>
                  <td colspan="7" class="px-6 py-3 text-right text-slate-700 dark:text-slate-200">Total Pendente
                    (Filtro):</td>
                  <td class="px-6 py-3 text-left text-slate-900 dark:text-white">{{
                    formatCurrency(totalParcelasVisiveis) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div class="block sm:hidden space-y-4">
          <div v-if="sortedGastos.length === 0" class="empty-state mt-6">Nenhuma compra encontrada.</div>
          <div v-for="gastoItem in sortedGastos" :key="`card-${gastoItem.id}`" class="mobile-card"
            :style="{ borderLeftColor: getBankColor(gastoItem.banco) }">
            <div class="flex justify-between items-start">
              <div class="flex-1 pr-4">
                <div class="flex items-center gap-3">
                  <img :src="getBankLogo(gastoItem.banco)" :alt="gastoItem.banco"
                    class="w-8 h-8 rounded-full shadow-sm flex-shrink-0 object-contain p-1 bg-white">
                  <div>
                    <p class="font-bold text-slate-800 dark:text-slate-100">{{ gastoItem.descricao }}</p>
                    <p class="text-sm text-slate-500 dark:text-slate-400">{{ gastoItem.banco }}</p>
                  </div>
                </div>
              </div>
              <div class="text-right">
                <p class="font-bold text-lg text-slate-900 dark:text-white">{{ formatCurrency(gastoItem.valorParcela,
                  false) }}</p>
                <p class="text-xs text-slate-500 dark:text-slate-400">Parc. {{ gastoItem.numeroParcela }}/{{
                  gastoItem.totalParcelas }}</p>
              </div>
            </div>
            <div
              class="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-base text-slate-500 dark:text-slate-400"
                  :title="gastoItem.categoria">{{ getCategoryIcon(gastoItem.categoria) }}</span>
                <span class="text-slate-700 dark:text-slate-300">{{ gastoItem.categoria }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-base text-slate-500 dark:text-slate-400">event</span>
                <span class="text-slate-700 dark:text-slate-300">{{ formatDate(gastoItem.dataPagamento) }}</span>
              </div>
              <div class="col-span-2 flex items-center gap-2">
                <span class="material-symbols-outlined text-base" :class="getStatusClass(gastoItem).text">{{
                  getStatusClass(gastoItem).icon }}</span>
                <select v-model="gastoItem.status" @change="updateStatus(gastoItem)" class="form-select-sm flex-1"
                  :class="getStatusClass(gastoItem).text">
                  <option value="PENDENTE">Pendente</option>
                  <option value="PAGO">Pago</option>
                  <option value="CONGELADO">Congelado</option>
                </select>
              </div>
            </div>
            <div class="mt-3 flex justify-end">
              <div class="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700/50 p-1">
                <button @click="handleEditParcela(gastoItem)" title="Editar Parcela"
                  class="btn-action-icon text-sky-600 dark:text-sky-400">
                  <span class="material-symbols-outlined text-lg">edit</span>
                </button>
                <button @click="confirmDelete(gastoItem)" title="Excluir Parcela"
                  class="btn-action-icon text-rose-500 dark:text-rose-400">
                  <span class="material-symbols-outlined text-lg">delete</span>
                </button>
              </div>
            </div>
          </div>
          <div v-if="sortedGastos.length > 0"
            class="panel mt-4 text-right font-semibold text-slate-800 dark:text-slate-100">
            Total Pendente: {{ formatCurrency(totalParcelasVisiveis) }}
          </div>
        </div>
      </div>
    </transition>

    <transition name="fade" mode="out-in">
      <div v-if="activeTab === 'analytics'" key="analytics">
        <div class="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div v-if="relatorioMensal?.gastosCartao" class="lg:col-span-5 panel">
            <h3 class="panel-title">Meta de Pagamento da Fatura ({{ nomeMesSelecionado }})</h3>
            <div class="flex justify-between items-baseline mb-1">
              <span class="text-sm font-medium text-slate-600 dark:text-slate-300">Progresso</span>
              <span class="text-sm font-bold"
                :class="relatorioMensal.gastosCartao.progressoPercentual >= 100 ? 'text-emerald-500' : 'text-[#5A189A] dark:text-[#D9BFFB]'">{{
                  relatorioMensal.gastosCartao.progressoPercentual.toFixed(0) }}%</span>
            </div>
            <div class="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-4">
              <div class="bg-[#5A189A] h-4 rounded-full transition-all duration-500"
                :style="{ width: relatorioMensal.gastosCartao.progressoPercentual + '%' }"></div>
            </div>
            <div class="text-xs text-slate-500 dark:text-slate-400 text-center mt-2">
              <span class="font-semibold text-emerald-600 dark:text-emerald-400">{{
                formatCurrency(relatorioMensal.gastosCartao.valorPago) }}</span>
              de
              <span class="font-semibold text-slate-700 dark:text-slate-200">{{
                formatCurrency(relatorioMensal.gastosCartao.valorTotal) }}</span>
            </div>
          </div>
          <div class="lg:col-span-2 panel text-center">
            <h3 class="panel-title">Fatura Atual (Pendente)</h3>
            <p class="text-4xl font-bold text-amber-500 dark:text-amber-400 my-4">{{
              formatCurrency(totalParcelasVisiveis) }}</p>
            <p class="text-sm text-slate-500 dark:text-slate-400">Soma das parcelas pendentes para os filtros
              selecionados.</p>
          </div>
          <div class="lg:col-span-3 panel text-center">
            <h3 class="panel-title">Dívida Total Futura</h3>
            <p class="text-4xl font-bold text-rose-500 dark:text-rose-400 my-4">{{ formatCurrency(dividaTotalFutura) }}
            </p>
            <p class="text-sm text-slate-500 dark:text-slate-400">Soma de todas as parcelas pendentes a partir de hoje.
            </p>
          </div>
          <div class="lg:col-span-3 panel">
            <h3 class="panel-title">Gastos por Categoria (Filtro)</h3>
            <div class="h-80">
              <Apexchart v-if="dadosGraficoCategoria.series.length > 0" type="donut" height="100%"
                :options="opcoesGraficoCategoria" :series="dadosGraficoCategoria.series" />
              <div v-else class="empty-state h-full">
                <span class="material-symbols-outlined text-5xl">pie_chart</span>
                <span>Sem dados para exibir.</span>
              </div>
            </div>
          </div>
          <div class="lg:col-span-2 panel">
            <h3 class="panel-title">Gastos por Banco (Filtro)</h3>
            <div class="h-80">
              <Apexchart v-if="dadosGraficoBanco.series.length > 0" type="bar" height="100%"
                :options="opcoesGraficoBanco" :series="dadosGraficoBanco.series" />
              <div v-else class="empty-state h-full">
                <span class="material-symbols-outlined text-5xl">account_balance</span>
                <span>Sem dados para exibir.</span>
              </div>
            </div>
          </div>
          <div class="lg:col-span-5 panel">
            <h3 class="panel-title">Previsão de Faturas (Próximos 6 Meses)</h3>
            <div class="h-80">
              <Bar v-if="dadosGraficoPrevisao.datasets[0].data.some(d => d > 0)" :data="dadosGraficoPrevisao"
                :options="opcoesGraficoPrevisao" />
              <div v-else class="empty-state h-full">
                <span class="material-symbols-outlined text-5xl">monitoring</span>
                <span>Não há parcelas futuras.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showModal" @click.self="closeModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div class="modal-panel max-w-lg">
          <header class="modal-header">
            <h3 class="modal-title">{{ isEditing ? 'Editar Parcela' : 'Adicionar Nova Compra' }}</h3>
            <button @click="closeModal" title="Fechar modal" :disabled="isSubmitting" class="btn-action-icon">
              <span class="material-symbols-outlined">close</span>
            </button>
          </header>
          <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="form-label">Banco</label>
                <select v-model="gastoCartao.banco" required class="form-select">
                  <option disabled value="">Selecione um banco</option>
                  <option v-for="name in Object.keys(bankColorVariants)" :key="name" :value="name">{{ name }}</option>
                </select>
              </div>
              <div class="relative" ref="categoryDropdownContainer">
                <label class="form-label">Categoria</label>
                <button @click="isCategoryDropdownOpen = !isCategoryDropdownOpen" type="button"
                  class="form-input flex items-center justify-between text-left">
                  <span v-if="gastoCartao.categoria" class="flex items-center gap-2">
                    <span class="material-symbols-outlined text-lg">{{ getCategoryIcon(gastoCartao.categoria) }}</span>
                    <span>{{ gastoCartao.categoria }}</span>
                  </span>
                  <span v-else class="text-slate-400 dark:text-slate-500">Selecione</span>
                  <span class="material-symbols-outlined text-slate-400">unfold_more</span>
                </button>
                <transition name="fade-fast">
                  <div v-if="isCategoryDropdownOpen"
                    class="absolute z-20 w-full mt-1 bg-white dark:bg-slate-900 rounded-lg shadow-lg border dark:border-slate-700 max-h-56 overflow-y-auto">
                    <ul>
                      <li v-for="(icon, name) in categoryIcons" :key="name" @click="selectCategory(name)"
                        class="px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-3">
                        <span class="material-symbols-outlined text-lg">{{ icon }}</span>
                        <span>{{ name }}</span>
                      </li>
                    </ul>
                  </div>
                </transition>
              </div>
            </div>
            <div>
              <label class="form-label">Descrição</label>
              <input v-model="gastoCartao.descricao" type="text" required placeholder="Ex: Compra na Amazon"
                class="form-input" />
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="form-label">{{ isEditing ? 'Valor da Parcela (R$)' : 'Valor Total (R$)' }}</label>
                <input v-if="isEditing" v-model.number="gastoCartao.valorParcela" type="number" step="0.01" required
                  placeholder="0.00" class="form-input" />
                <input v-else v-model.number="gastoCartao.valorTotal" type="number" step="0.01" required
                  placeholder="0.00" class="form-input" />
              </div>
              <div>
                <label class="form-label">{{ isEditing ? 'Data de Vencimento' : 'Data da 1ª Parcela' }}</label>
                <input v-model="gastoCartao.dataPagamento" type="date" required class="form-input" />
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div v-if="!isEditing">
                <label class="form-label">Nº de Parcelas</label>
                <input v-model.number="gastoCartao.numeroParcelas" type="number" min="1" placeholder="1"
                  class="form-input" />
              </div>
              <div>
                <label class="form-label">Status</label>
                <select v-model="gastoCartao.status" required class="form-select">
                  <option value="PENDENTE">Pendente</option>
                  <option value="PAGO">Pago</option>
                  <option value="CONGELADO">Congelado</option>
                </select>
              </div>
            </div>
            <div v-if="isEditing" class="pt-2">
              <p class="form-label mb-2">Aplicar alterações em:</p>
              <div class="flex items-center space-x-4">
                <label class="flex items-center cursor-pointer">
                  <input type="radio" v-model="gastoCartao.tipoEdicao" value="UNICO" class="form-radio">
                  <span class="ml-2 text-sm">Apenas nesta parcela</span>
                </label>
                <label class="flex items-center cursor-pointer">
                  <input type="radio" v-model="gastoCartao.tipoEdicao" value="TODOS" class="form-radio">
                  <span class="ml-2 text-sm">Nesta e nas próximas</span>
                </label>
              </div>
            </div>
            <footer class="pt-4 flex justify-end gap-3">
              <button @click="closeModal" type="button" :disabled="isSubmitting"
                class="btn btn-secondary">Cancelar</button>
              <button type="submit" :disabled="isSubmitting" class="btn btn-primary">
                <span v-if="isSubmitting" class="material-symbols-outlined animate-spin mr-2">progress_activity</span>
                {{ isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Adicionar') }}
              </button>
            </footer>
          </form>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showDeleteConfirm" @click.self="showDeleteConfirm = false"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div class="modal-panel max-w-md">
          <div class="p-6 text-center">
            <span class="material-symbols-outlined text-5xl text-rose-500 dark:text-rose-400 mx-auto">warning</span>
            <h3 class="mt-2 text-xl font-semibold">Excluir Parcela(s)</h3>
            <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">Escolha como deseja excluir as parcelas desta
              compra.</p>
          </div>
          <div class="p-6 pt-0 space-y-3">
            <button @click="deleteParcela('UNICO')" class="btn btn-secondary w-full">Apenas Esta Parcela</button>
            <button @click="deleteParcela('TODOS')" class="btn w-full bg-rose-600 hover:bg-rose-700 text-white">Esta e
              as Próximas</button>
            <button @click="showDeleteConfirm = false"
              class="btn w-full !bg-transparent !shadow-none !ring-0 text-slate-600 dark:text-slate-300 hover:!bg-slate-100 dark:hover:!bg-slate-700/50">Cancelar</button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showUnsavedChangesConfirm" @click.self="showUnsavedChangesConfirm = false"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div class="modal-panel max-w-md">
          <div class="p-8 text-center">
            <span class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">warning</span>
            <h3 class="mt-4 text-xl font-semibold">Descartar Alterações?</h3>
            <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">Você possui alterações não salvas. Tem certeza
              que deseja sair?</p>
            <div class="mt-6 flex justify-center gap-3">
              <button @click="showUnsavedChangesConfirm = false" type="button"
                class="btn btn-secondary">Cancelar</button>
              <button @click="handleConfirmClose" class="btn bg-rose-600 hover:bg-rose-700 text-white">Sim,
                Descartar</button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUnmounted, defineAsyncComponent } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'

const Bar = defineAsyncComponent(() => import('vue-chartjs').then(module => module.Bar))
const Apexchart = defineAsyncComponent(() => import('vue3-apexcharts'))

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

const router = useRouter()
const allGastos = ref([])
const showModal = ref(false)
const showDeleteConfirm = ref(false)
const isEditing = ref(false)
const isSubmitting = ref(false)
const isCategoryDropdownOpen = ref(false)
const categoryDropdownContainer = ref(null)
const pristineGastoCartao = ref(null)
const showUnsavedChangesConfirm = ref(false)
const activeTab = ref('list')
const relatorioMensal = ref(null)

const isMobile = ref(false)
const checkDevice = () => {
  isMobile.value = window.innerWidth < 640
}

const isDarkMode = computed(() => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)

const isFormDirty = computed(() => {
  if (!pristineGastoCartao.value) return false
  return JSON.stringify(gastoCartao.value) !== JSON.stringify(pristineGastoCartao.value)
})

const statusFiltro = ref('')
const bancoFiltro = ref('')
const categoriaFiltro = ref('')
const mesSelecionado = ref('')
const anoSelecionado = ref('')

const bankColorVariants = {
  Santander: { color: { light: '#EF0000', dark: '#F44336' } },
  Nubank: { color: { light: '#820AD1', dark: '#9C27B0' } },
  'Banco do Brasil': { color: { light: '#FFDF00', dark: '#FFEB3B' } },
  'C6 Bank': { color: { light: '#333333', dark: '#CCCCCC' } },
  Itaú: { color: { light: '#EC7000', dark: '#FF9800' } },
  Bradesco: { color: { light: '#CC092F', dark: '#E53935' } },
  Caixa: { color: { light: '#0064B4', dark: '#03A9F4' } },
  Inter: { color: { light: '#FF7A00', dark: '#FFB74D' } },
  PagBank: { color: { light: '#FFC800', dark: '#FFCA28' } },
  PicPay: { color: { light: '#21C25E', dark: '#4CAF50' } },
  'Mercado Pago': { color: { light: '#00B2F2', dark: '#29B6F6' } },
  Renner: { color: { light: '#D92A2F', dark: '#E53935' } },
  Amazon: { color: { light: '#FF9900', dark: '#FFB300' } },
  Shopee: { color: { light: '#EE4D2D', dark: '#FF7043' } },
  Outro: { color: { light: '#94A3B8', dark: '#90A4AE' } }
}

const categoryIcons = {
  Alimentação: 'restaurant',
  Transporte: 'commute',
  Moradia: 'home',
  Lazer: 'sports_esports',
  Saúde: 'health_and_safety',
  Educação: 'school',
  Compras: 'shopping_bag',
  Serviços: 'dns',
  Viagem: 'flight',
  Padrão: 'credit_card'
}

const bankLogos = {
  Santander: 'https://assets.hgbrasil.com/finance/companies/big/santander-br.png',
  Nubank: 'https://logodownload.org/wp-content/uploads/2019/08/nubank-logo-2-1.png',
  'Banco do Brasil': 'https://logopng.com.br/logos/banco-do-brasil-5.png',
  'C6 Bank': 'https://www.jornalcontabil.com.br/wp-content/uploads/2019/11/c6-1200x1200.png',
  Itaú: 'https://upload.wikimedia.org/wikipedia/commons/2/2d/2023_Ita%C3%BA_Unibanco_Logo.png',
  Bradesco: 'https://logodownload.org/wp-content/uploads/2018/09/bradesco-logo-4.png',
  Caixa: 'https://www.freepnglogos.com/uploads/logo-caixa-png/caixa-download-png-5.png',
  Inter: 'https://yt3.googleusercontent.com/c_fQMiJzVdFb80Mr79X5pJzLw2VoFtVab3fLLcUqJzpe-cmNti04vLsQ7bDVGcpHa0UMlrjg-KU=s900-c-k-c0x00ffffff-no-rj',
  PagBank: 'https://yt3.googleusercontent.com/dhhzAXfQDqT4ubXWVCcxRCT5WE6AvmZjARlYrMwmfel8WvLcqNSKdl4Ijya9m73UVIoIyZeV=s900-c-k-c0x00ffffff-no-rj',
  PicPay: 'https://play-lh.googleusercontent.com/pTvc9kCumx_24eJDwGUpvcBwljcIBkrsL3qHwhBW2NalMQ-XxTtHRV9YAJanBxkV0Rw',
  'Mercado Pago': 'https://cdn.shopify.com/app-store/listing_images/4f3dc60a0ef8beee2a168713fe20181f/icon/CIHzwOjZsI0DEAE=.png',
  Renner: 'https://logodownload.org/wp-content/uploads/2014/07/renner-logo-6.png',
  Amazon: 'https://images.icon-icons.com/1195/PNG/512/1490889698-amazon_82521.png',
  Shopee: 'https://logospng.org/download/shopee/logo-shopee-icon-1024.png',
  Outro: 'https://w7.pngwing.com/pngs/52/580/png-transparent-bank-account-computer-icons-bank-text-payment-logo.png'
}

const getBankColor = (banco) => (bankColorVariants[banco] || bankColorVariants.Outro).color[isDarkMode.value ? 'dark' : 'light']
const getBankLogo = (banco) => bankLogos[banco] || bankLogos.Outro
const getCategoryIcon = (category) => categoryIcons[category] || categoryIcons.Padrão

const getInitialGastoCartao = () => ({
  id: null,
  descricao: '',
  categoria: 'Padrão',
  banco: '',
  status: 'PENDENTE',
  valorTotal: null,
  valorParcela: null,
  numeroParcelas: 1,
  dataPagamento: '',
  tipoEdicao: 'UNICO',
  dataQuitacaoReal: null
})

const gastoCartao = ref(getInitialGastoCartao())
const itemParaDeletar = ref(null)

const apiBaseUrl = 'https://backend.controlease.com.br'
const apiUrl = `${apiBaseUrl}/gastos-cartao`
const relatorioApiUrl = `${apiBaseUrl}/relatorios/despesas-mensais`

const meses = [
  { nome: 'Jan', valor: '01' }, { nome: 'Fev', valor: '02' },
  { nome: 'Mar', valor: '03' }, { nome: 'Abr', valor: '04' },
  { nome: 'Mai', valor: '05' }, { nome: 'Jun', valor: '06' },
  { nome: 'Jul', valor: '07' }, { nome: 'Ago', valor: '08' },
  { nome: 'Set', valor: '09' }, { nome: 'Out', valor: '10' },
  { nome: 'Nov', valor: '11' }, { nome: 'Dez', valor: '12' }
]

const getMesAtual = () => String(new Date().getMonth() + 1).padStart(2, '0')
const getAnoAtual = () => String(new Date().getFullYear())

const authConfig = () => {
  const token = localStorage.getItem('token')
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {}
}

const fetchGastosCartao = async () => {
  try {
    const response = await axios.get(apiUrl, authConfig())
    allGastos.value = response.data
  } catch (error) {
    console.error('Erro ao buscar gastos do cartão:', error)
  }
}

const fetchRelatorioMensal = async () => {
  if (!mesSelecionado.value || !anoSelecionado.value) {
    relatorioMensal.value = null
    return
  }
  try {
    const params = { mes: mesSelecionado.value, ano: anoSelecionado.value }
    const response = await axios.get(relatorioApiUrl, { ...authConfig(), params })
    relatorioMensal.value = response.data
  } catch (error) {
    console.error('Erro ao buscar relatório mensal:', error)
    relatorioMensal.value = null
  }
}

watch([mesSelecionado, anoSelecionado, activeTab], () => {
  if (activeTab.value === 'analytics') {
    fetchRelatorioMensal()
  }
}, { immediate: true })

const nomeMesSelecionado = computed(() => {
  if (!mesSelecionado.value || !anoSelecionado.value) return ''
  const mesObj = meses.find(m => m.valor === mesSelecionado.value)
  return `${mesObj?.nome}/${anoSelecionado.value}`
})

const filteredGastos = computed(() => {
  return allGastos.value.filter(gasto => {
    const [year, month] = gasto.dataPagamento.split('-')
    const mesOk = !mesSelecionado.value || month === mesSelecionado.value
    const anoOk = !anoSelecionado.value || year === anoSelecionado.value
    const statusOk = !statusFiltro.value || gasto.status === statusFiltro.value
    const bancoOk = !bancoFiltro.value || gasto.banco === bancoFiltro.value
    const categoriaOk = !categoriaFiltro.value || gasto.categoria === categoriaFiltro.value
    return mesOk && anoOk && statusOk && bancoOk && categoriaOk
  })
})

const sortedGastos = computed(() => {
  return [...filteredGastos.value].sort((a, b) => new Date(a.dataPagamento) - new Date(b.dataPagamento))
})

const anosDisponiveis = computed(() => {
  const anos = new Set(allGastos.value.map(g => g.dataPagamento?.split('-')[0]).filter(Boolean))
  return Array.from(anos).sort((a, b) => parseInt(b, 10) - parseInt(a, 10))
})

const bancosDisponiveis = computed(() => {
  const bancos = new Set(allGastos.value.map(g => g.banco).filter(Boolean))
  return Array.from(bancos).sort()
})

const categoriasDisponiveis = computed(() => {
  const categorias = new Set(allGastos.value.map(g => g.categoria).filter(Boolean))
  return Array.from(categorias).sort()
})

const totalParcelasVisiveis = computed(() =>
  (filteredGastos.value || []).reduce((total, g) => g.status === 'PENDENTE' ? total + (parseFloat(g.valorParcela) || 0) : total, 0)
)

const dividaTotalFutura = computed(() => {
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  return allGastos.value
    .filter(g => g.status === 'PENDENTE' && new Date(g.dataPagamento) >= hoje)
    .reduce((total, g) => total + g.valorParcela, 0)
})

const gastosPorCategoria = computed(() => {
  const gastos = {}
  filteredGastos.value.filter(g => g.status === 'PENDENTE').forEach(g => {
    gastos[g.categoria] = (gastos[g.categoria] || 0) + g.valorParcela
  })
  return gastos
})

const dadosGraficoCategoria = computed(() => {
  const sorted = Object.entries(gastosPorCategoria.value).sort(([, a], [, b]) => b - a)
  return {
    series: sorted.map(([, valor]) => valor),
    labels: sorted.map(([nome]) => nome)
  }
})

const opcoesGraficoCategoria = computed(() => ({
  chart: { type: 'donut', background: 'transparent' },
  labels: dadosGraficoCategoria.value.labels,
  colors: ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#EC4899'],
  legend: {
    position: 'bottom',
    fontFamily: 'Inter, sans-serif',
    labels: { colors: isDarkMode.value ? '#CBD5E1' : '#475569' }
  },
  tooltip: { theme: isDarkMode.value ? 'dark' : 'light', y: { formatter: (val) => formatCurrency(val) } },
  stroke: { width: 0 },
  dataLabels: { enabled: false },
  plotOptions: { pie: { donut: { labels: { show: true, total: { show: true, label: 'Total', color: isDarkMode.value ? '#CBD5E1' : '#475569', formatter: (w) => formatCurrency(w.globals.seriesTotals.reduce((a, b) => a + b, 0)) } } } } }
}))

const gastosPorBanco = computed(() => {
  const gastos = {}
  filteredGastos.value.filter(g => g.status === 'PENDENTE').forEach(g => {
    gastos[g.banco] = (gastos[g.banco] || 0) + g.valorParcela
  })
  return Object.entries(gastos).sort(([, a], [, b]) => a - b)
})

const dadosGraficoBanco = computed(() => ({
  series: [{ name: 'Gasto', data: gastosPorBanco.value.map(([, valor]) => valor) }],
  labels: gastosPorBanco.value.map(([nome]) => nome)
}))

const opcoesGraficoBanco = computed(() => ({
  chart: { type: 'bar', background: 'transparent', toolbar: { show: false } },
  plotOptions: { bar: { borderRadius: 4, horizontal: true, distributed: true, barHeight: '60%' } },
  dataLabels: { enabled: false },
  colors: dadosGraficoBanco.value.labels.map(nome => getBankColor(nome)),
  xaxis: { categories: dadosGraficoBanco.value.labels, labels: { style: { colors: isDarkMode.value ? '#9CA3AF' : '#6B7280' } } },
  yaxis: { labels: { style: { colors: isDarkMode.value ? '#9CA3AF' : '#6B7280' } } },
  legend: { show: false },
  grid: { borderColor: isDarkMode.value ? '#374151' : '#E5E7EB', xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
  tooltip: { theme: isDarkMode.value ? 'dark' : 'light', y: { formatter: (val) => formatCurrency(val) } }
}))

const dadosGraficoPrevisao = computed(() => {
  const labels = []
  const data = []
  const hoje = new Date()
  for (let i = 0; i < 6; i++) {
    const dataFutura = new Date(hoje.getFullYear(), hoje.getMonth() + i, 1)
    labels.push(dataFutura.toLocaleString('pt-BR', { month: 'short', year: '2-digit' }))
    const ano = dataFutura.getFullYear()
    const mes = String(dataFutura.getMonth() + 1).padStart(2, '0')
    const totalMes = allGastos.value
      .filter(g => g.status === 'PENDENTE' && g.dataPagamento.startsWith(`${ano}-${mes}`))
      .reduce((sum, g) => sum + g.valorParcela, 0)
    data.push(totalMes)
  }
  return {
    labels,
    datasets: [{ label: 'Fatura Prevista', backgroundColor: '#8B5CF6', data, borderRadius: 4 }]
  }
})

const opcoesGraficoPrevisao = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: { grid: { color: isDarkMode.value ? 'rgba(148, 163, 184, 0.1)' : 'rgba(226, 232, 240, 1)' }, ticks: { color: isDarkMode.value ? '#94a3b8' : '#64748b' } },
    x: { grid: { display: false }, ticks: { color: isDarkMode.value ? '#94a3b8' : '#64748b' } }
  },
  plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}` } } }
}))

const exportToCSV = () => {
  if (sortedGastos.value.length === 0) {
    alert('Não há dados para exportar com os filtros atuais.')
    return
  }
  const headers = ['Descrição', 'Banco', 'Categoria', 'Parcela Atual', 'Total de Parcelas', 'Valor da Parcela', 'Data de Vencimento', 'Data de Quitação', 'Status']
  const formatCSVField = (field) => `"${String(field ?? '').replace(/"/g, '""')}"`
  const rows = sortedGastos.value.map(g => [g.descricao, g.banco, g.categoria, g.numeroParcela, g.totalParcelas, g.valorParcela, formatDate(g.dataPagamento), formatDate(g.dataQuitacaoReal), g.status].map(formatCSVField).join(','))
  const csv = '\uFEFF' + headers.join(',') + '\n' + rows.join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `gastos_cartao_${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(link); link.click(); document.body.removeChild(link)
}

const handleNovaCompra = () => {
  if (isMobile.value) {
    router.push({ name: 'GastoCartaoAdd' }) 
  } else {
    showAddCompraModal()
  }
}

const handleEditParcela = (item) => {
  if (isMobile.value) {
    router.push({ name: 'GastoCartaoEdit', params: { id: item.id } }) 
  } else {
    editParcela(item) 
  }
}

const showAddCompraModal = () => {
  resetForm()
  pristineGastoCartao.value = JSON.parse(JSON.stringify(gastoCartao.value))
  showModal.value = true
}

const handleSubmit = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true
  try {
    if (isEditing.value) {
      await axios.put(`${apiUrl}/${gastoCartao.value.id}`, gastoCartao.value, authConfig())
    } else {
      const payload = { ...gastoCartao.value, numeroParcelas: Math.max(1, parseInt(gastoCartao.value.numeroParcelas, 10) || 1) }
      await axios.post(apiUrl, payload, authConfig())
    }
    await fetchGastosCartao()
    if (activeTab.value === 'analytics') await fetchRelatorioMensal()
    forceCloseModal()
  } catch (error) {
    console.error('Erro ao salvar:', error)
    alert('Erro ao salvar.')
  } finally {
    isSubmitting.value = false
  }
}

const editParcela = (parcelaToEdit) => {
  gastoCartao.value = { ...parcelaToEdit, tipoEdicao: 'UNICO' }
  pristineGastoCartao.value = JSON.parse(JSON.stringify(gastoCartao.value))
  isEditing.value = true
  showModal.value = true
}

const confirmDelete = (parcela) => {
  itemParaDeletar.value = parcela
  showDeleteConfirm.value = true
}

const deleteParcela = async (tipoDelecao) => {
  if (!itemParaDeletar.value) return
  try {
    await axios.delete(`${apiUrl}/${itemParaDeletar.value.id}?tipoDelecao=${tipoDelecao}`, authConfig())
    await fetchGastosCartao()
    if (activeTab.value === 'analytics') await fetchRelatorioMensal()
  } catch (error) {
    console.error('Erro ao excluir:', error)
    alert('Erro ao excluir.')
  } finally {
    showDeleteConfirm.value = false
    itemParaDeletar.value = null
  }
}

const closeModal = () => {
  if (isSubmitting.value) return
  if (isFormDirty.value) {
    showUnsavedChangesConfirm.value = true
  } else {
    forceCloseModal()
  }
}

const forceCloseModal = () => {
  showModal.value = false
  resetForm()
}

const handleConfirmClose = () => {
  showUnsavedChangesConfirm.value = false
  forceCloseModal()
}

const updateStatus = async (gastoToUpdate) => {
  try {
    const payload = { ...gastoToUpdate, tipoEdicao: 'UNICO' }
    await axios.put(`${apiUrl}/${gastoToUpdate.id}`, payload, authConfig())
    await fetchGastosCartao()
    if (activeTab.value === 'analytics') await fetchRelatorioMensal()
  } catch (error) {
    console.error('Erro ao atualizar status:', error)
    alert('Erro ao atualizar status.')
    await fetchGastosCartao()
  }
}

const resetForm = () => {
  gastoCartao.value = getInitialGastoCartao()
  isEditing.value = false
  pristineGastoCartao.value = null
}

const formatCurrency = (value, withSymbol = true) => {
  if (typeof value !== 'number') value = 0
  const options = { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  if (withSymbol) {
    options.style = 'currency'
    options.currency = 'BRL'
  }
  return value.toLocaleString('pt-BR', options)
}

const formatDate = (dateString) => {
  if (!dateString) return '—'
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return 'N/A'
  return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' })
}

const getStatusClass = (gastoItem) => {
  if (gastoItem.status === 'PENDENTE') return { text: 'text-amber-600 dark:text-amber-400 font-semibold', icon: 'pending' }
  if (gastoItem.status === 'PAGO') return { text: 'text-green-600 dark:text-green-400 font-semibold', icon: 'check_circle' }
  if (gastoItem.status === 'CONGELADO') return { text: 'text-sky-600 dark:text-sky-400', icon: 'ac_unit' }
  return { text: 'text-slate-600 dark:text-slate-300', icon: 'help' }
}

const selectCategory = (name) => {
  gastoCartao.value.categoria = name
  isCategoryDropdownOpen.value = false
}

const handleClickOutside = (event) => {
  if (categoryDropdownContainer.value && !categoryDropdownContainer.value.contains(event.target)) {
    isCategoryDropdownOpen.value = false
  }
}

watch(isCategoryDropdownOpen, (isOpen) => {
  if (isOpen) document.addEventListener('click', handleClickOutside)
  else document.removeEventListener('click', handleClickOutside)
})

onMounted(() => {
  mesSelecionado.value = getMesAtual()
  anoSelecionado.value = getAnoAtual()
  fetchGastosCartao()
  fetchRelatorioMensal()
  checkDevice()
  window.addEventListener('resize', checkDevice)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', checkDevice)
})
</script>

<style>
.form-input {
  @apply w-full px-3 py-2 text-sm text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700/50 border border-solid border-slate-300 dark:border-slate-600 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500/70 focus:border-purple-500;
}

.form-select {
  @apply appearance-none block w-full px-3 py-2.5 text-sm font-normal text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700/50 bg-clip-padding bg-no-repeat border border-solid border-slate-300 dark:border-slate-600 rounded-lg transition ease-in-out focus:text-slate-700 focus:bg-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50;
}

.form-select-sm {
  @apply text-xs appearance-none block w-full px-2 py-1 font-normal bg-transparent border-0 focus:ring-0 focus:outline-none rounded-md cursor-pointer;
}

.form-radio {
  @apply focus:ring-purple-500 h-4 w-4 text-purple-600 border-slate-300 dark:border-slate-500 dark:bg-slate-600 dark:checked:bg-purple-500;
}

.btn-action-icon {
  @apply h-8 w-8 grid place-items-center rounded-lg transition-colors text-slate-500 dark:text-slate-400 hover:bg-slate-200/70 dark:hover:bg-slate-700/50;
}

.btn {
  @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60;
}

.btn-primary {
  @apply btn bg-[#5A189A] hover:bg-[#3C096C] text-white shadow-lg shadow-[#5A189A]/20;
}

.btn-secondary {
  @apply btn bg-white/70 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-20px);
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  @apply flex flex-col items-center justify-center text-center text-slate-500 dark:text-slate-400 p-4 min-h-[100px];
}

.panel {
  @apply bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20 p-4 sm:p-5;
}

.modal-panel {
  @apply bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl w-full ring-1 ring-black/10 dark:ring-white/10;
}

.modal-header {
  @apply px-6 py-4 border-b border-slate-200/80 dark:border-slate-700/60 flex justify-between items-center;
}

.modal-title {
  @apply text-xl font-semibold text-slate-800 dark:text-slate-100;
}

.mobile-card {
  @apply bg-white dark:bg-slate-800/80 shadow-lg rounded-lg p-4 border-l-4;
}

.segmented-control {
  @apply inline-flex rounded-2xl p-1 bg-slate-200/70 dark:bg-slate-800/80;
}

.segmented-control button {
  @apply inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 text-sm font-semibold rounded-xl transition-all;
}

.segmented-control button.active {
  @apply bg-white dark:bg-slate-700 text-[#3C096C] dark:text-[#D9BFFB] shadow-md;
}

.segmented-control button:not(.active) {
  @apply text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white;
}

.panel-title {
  @apply text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4;
}

.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity .15s ease;
}

.fade-fast-enter-from,
.fade-fast-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
